// React context for the active UI locale.
//
// Wrap the app in `<LocaleProvider>` (see App.tsx) and consume via `useLocale()`
// or, more commonly, the `useT()` translator hook from `./use-t`.
//
// Persistence model:
//   - Initial value comes from `detectInitialLocale()` (localStorage > nav lang
//     > default).
//   - Every `setLocale()` writes to localStorage AND updates `<html lang>` so
//     screen readers + Chrome translate detection get the right hint.
//   - DB sync happens elsewhere (account settings page) via the
//     `useUpdateAccountProfile()` mutation — keeping that out of here means
//     this provider doesn't need to know about React Query.

import { DEFAULT_LOCALE, type Locale, SUPPORTED_LOCALES } from '@ohear/shared'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { detectInitialLocale, writeStoredLocale } from './detect-locale'

interface LocaleContextValue {
  /** Active UI locale. */
  locale: Locale
  /** Set + persist the active locale. */
  setLocale: (l: Locale) => void
  /** List the language switcher should iterate. */
  supported: readonly Locale[]
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => detectInitialLocale())

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l)
    writeStoredLocale(l)
    if (typeof document !== 'undefined') {
      document.documentElement.lang = l
    }
  }, [])

  // Keep `<html lang>` in sync on mount + whenever locale changes (covers the
  // initial render where setLocale hasn't been called yet).
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale
    }
  }, [locale])

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, supported: SUPPORTED_LOCALES }),
    [locale, setLocale],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext)
  if (!ctx) {
    // Fallback for tests / Storybook stories that mount components outside the
    // provider — return a static no-op rather than throwing, so unit tests
    // don't have to wrap every render.
    return {
      locale: DEFAULT_LOCALE,
      setLocale: () => {},
      supported: SUPPORTED_LOCALES,
    }
  }
  return ctx
}
